import type { Actor, Problem } from "@baton/api";
import type { ReactNode } from "react";
import type { PopoverAnchor } from "../overlays/Popover/Popover.tsx";

/**
 * § Pickers, `research.md` W-13: one `Picker` implementation of the WAI ARIA combobox
 * pattern, specialised by prop. The pickers are presentational: the screens of `apps/web`
 * hand them their options from the query cache and receive the change, so no picker calls
 * the network and every one is used identically in the list row, the item detail, the bulk
 * bar and the palette (FR-015-021, FR-015-101).
 */
export interface PickerOption<V extends string = string> {
  readonly value: V;
  readonly label: string;
  /** Extra text the search matches beside the label: an identifier, a kind, a group. */
  readonly keywords?: readonly string[];
  /** A leading glyph: an avatar, a colour swatch, a priority mark, a state-group mark. */
  readonly icon?: ReactNode;
  /** Secondary text after the label: an agent kind, a project's identifier, a state group. */
  readonly hint?: string;
  readonly disabled?: boolean;
  /** Shown on hover and focus of a disabled option: an inactive agent's reason, a refused transition's failed condition. */
  readonly disabledReason?: string;
  /** The `PickerGroup` the option is listed under. */
  readonly group?: string;
}

export interface PickerGroup {
  readonly id: string;
  readonly label: string;
}

export type PickerStatus =
  | { readonly kind: "ready" }
  | { readonly kind: "loading" }
  | { readonly kind: "searching" }
  | { readonly kind: "error"; readonly problem: Problem; readonly onRetry: () => void };

export interface PickerCreate<V extends string> {
  /** The create row's words for the typed text, naming what it makes: "Add “Marta” as a contact" (`contracts/design-system.md` § Pickers, 2026-09-27). */
  readonly label: (query: string) => string;
  /** Create through the API and answer the option to select; a rejection's `message`, the problem's detail, is said in the row. */
  readonly onCreate: (query: string) => Promise<PickerOption<V>>;
  /** The row's add glyph for what it makes, the plus when absent (the person-plus for a contact). */
  readonly icon?: ReactNode;
  /** Shown as a quiet row while nothing is typed, so a person sees that a new entry can be made here before they type one (2026-09-18, by the owner's report: with an empty search nothing said a label could be created). */
  readonly hint?: string;
}

/**
 * An action a picker offers on one of its options from inside the panel, taken in two steps (the
 * QA call of 2026-09-28: a client group is deleted from the Group field itself). The first step is
 * a button in the panel's foot naming the highlighted option ("Delete group ‘Group A’"), or its
 * shortcut from the search field; the second says what the action will do and asks for the
 * action again before anything is written. It lives in the foot rather than inside the row, so the
 * listbox holds options and nothing else.
 */
export interface PickerOptionAction<V extends string> {
  /** The first step's words for the option: "Delete group ‘Group A’". */
  readonly label: (option: PickerOption<V>) => string;
  /** The second step's words: what the action does, before it is done. */
  readonly consequence: (option: PickerOption<V>) => string;
  /** The second step's button, which runs it: "Delete group". */
  readonly confirmLabel: string;
  readonly icon?: ReactNode;
  /** Whether the option offers the action; every option does when absent. */
  readonly offered?: (option: PickerOption<V>) => boolean;
  /** Runs the action; a rejection's `message` is said in the foot and the second step stays. */
  readonly run: (option: PickerOption<V>) => Promise<void>;
}

export interface PickerProps<V extends string = string> {
  /** The accessible name of the combobox. */
  readonly label: string;
  readonly options: readonly PickerOption<V>[];
  readonly groups?: readonly PickerGroup[];
  /** Always an array: a single-select picker holds zero or one value. */
  readonly value: readonly V[];
  readonly onChange: (value: readonly V[]) => void;
  readonly multiple?: boolean;
  /** Default true; `PriorityPicker` is the one fixed list with no search. */
  readonly searchable?: boolean;
  readonly searchPlaceholder?: string;
  /** Remote search: the host refetches the options for the query (workspace members, items). */
  readonly onSearch?: (query: string) => void;
  readonly creatable?: PickerCreate<V>;
  /** A destructive action on the highlighted option, taken in two steps in the panel's foot. */
  readonly optionAction?: PickerOptionAction<V>;
  readonly emptyMessage: string;
  /** The line for a query nothing matches, or its words for the query ("No contact matches “Marta”"). */
  readonly noResultsMessage?: string | ((query: string) => string);
  readonly status?: PickerStatus;
  readonly disabled?: boolean;
  /**
   * A row that clears the value ("No assignee", "No cycle"). It reads as chosen while the value is
   * empty, unless the host says otherwise through `checked`: a filter's empty value means nothing is
   * chosen yet, which is not the same as its "No client" being chosen.
   */
  readonly none?: { readonly label: string; readonly checked?: boolean };
  /** What the popover anchors to: the property chip, the row control, the bulk bar button. */
  readonly trigger?: ReactNode;
  /**
   * Where a picker with no trigger of its own stands, as `Popover`'s `anchor` (2026-10-03): a menu's row
   * opens it ("Link to task…" in an insight's "⋯"), so it stands on the menu's trigger, `open` holds it,
   * and the focus goes back to that trigger when it closes. A picker takes a trigger or an anchor.
   */
  readonly anchor?: PopoverAnchor;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly renderOption?: (option: PickerOption<V>, state: { readonly selected: boolean }) => ReactNode;
  readonly footer?: ReactNode;
  /** Controls under the search and above the results, such as a scope filter (2026-09-17). */
  readonly header?: ReactNode;
  /** Default: the opposite of `multiple`. */
  readonly closeOnSelect?: boolean;
  readonly placement?: "top" | "bottom" | "left" | "right";
  readonly align?: "start" | "center" | "end";
  readonly className?: string;
}

/** The actor rows of `GET .../members`: the actor shape plus its activity and, for an inactive agent, the reason it is listed disabled. */
export interface PickerActor extends Actor {
  readonly is_active?: boolean;
  readonly inactive_reason?: string;
}

export interface StateOption {
  readonly id: string;
  readonly name: string;
  readonly group: string;
  readonly color: string;
  readonly handoff_agent?: Actor | null;
}

/** One entry of `available_transitions` (001 `expand=transitions`), as the state control reads it. */
export interface TransitionOption {
  readonly to_state_id: string;
  readonly allowed: boolean;
  /** The failed condition in words, from `@baton/core`'s `describeTransitionRefusal`. */
  readonly reason?: string;
}

export interface LabelOption {
  readonly id: string;
  readonly name: string;
  /** Null or absent where the label takes its shade of the team's colour (001 FR-033). */
  readonly color?: string | null;
  readonly parent_id?: string | null;
}

export interface CycleOption {
  readonly id: string;
  readonly name: string;
  readonly status: "active" | "upcoming" | "completed";
}

export interface ModuleOption {
  readonly id: string;
  readonly name: string;
}

export interface ItemOption {
  readonly id: string;
  readonly identifier: string;
  readonly title: string;
  /** `color` is the state's own colour, which its glyph is drawn in as the list's state mark is; a caller that has none leaves the glyph in the text colour. */
  readonly state: { readonly name: string; readonly group: string; readonly color?: string };
}

export const PRIORITIES = ["urgent", "high", "medium", "low", "none"] as const;
export type Priority = (typeof PRIORITIES)[number];

export const STATE_GROUPS = ["backlog", "unstarted", "agent", "started", "review", "completed", "cancelled"] as const;
export type StateGroup = (typeof STATE_GROUPS)[number];
